import { Grid, Stack, Typography } from '@mui/material';
import {
  ClockClockwiseIcon,
  CurrencyEurIcon,
  GenderFemaleIcon,
  MotorcycleIcon,
  TimerIcon,
  WheelchairIcon,
} from '@phosphor-icons/react';

import CarIcon from '@/assets/icons/car.svg?react';
import Lighting from '@/assets/icons/lighting.svg?react';
import WheelchairRamp from '@/assets/icons/wheelchair-ramp.svg?react';
import { StatNumber } from '@/components/common/StatNumber';
import { CircleIconValue } from '@/components/map/details/CircleIconValue';
import { ForecastBars } from '@/components/map/details/ForecastBars';
import type {
  ParkAndRideFacility,
  ParkAndRideFacilityData,
  ParkingFacility,
  ParkingFacilityData,
} from '@/modules/api/backend';
import type { ForecastItem } from '@/types/Forecast';

const ICON_SIZE = 24;

interface DetailCardParkingFacilityContentProps {
  facility: ParkingFacility | ParkAndRideFacility;
  data?: ParkingFacilityData | ParkAndRideFacilityData;
  forecast?: ForecastItem[];
}

export function BaseParkingFacilityDetailContent({ facility, data, forecast }: DetailCardParkingFacilityContentProps) {
  return (
    <Grid container flexWrap="nowrap" py={1.5} spacing={1.5}>
      <Grid display="flex" flex={1} flexDirection="column" justifyContent="space-between" size={6}>
        <Stack direction="column" gap={0.5} height="100%" justifyContent="center" my={1} pl={2}>
          <Stack alignItems="center" direction="row" justifyContent="flex-start" spacing={1}>
            <CarIcon />
            <StatNumber>{data?.numVacantSpaces ?? facility.numParkingSpaceTotal ?? '?'}</StatNumber>
            {data ? (
              <Typography
                alignSelf="end"
                color="textSecondary"
                style={{ marginBottom: 2 }}
                variant="body2"
              >{`/ ${facility.numParkingSpaceTotal ?? '?'}`}</Typography>
            ) : null}
          </Stack>
          <Stack alignItems="center" direction="row" pl={0.75} spacing={0.5}>
            {data ? (
              <Typography
                color="success.main"
                sx={{ display: 'inline', '&::before': { content: '"•"', marginRight: '0.75rem' } }}
              >
                Live
              </Typography>
            ) : null}
            <Typography
              title={data ? 'Freie Plätze (Live)' : 'Auto Stellplätze'}
              sx={{
                display: 'inline',
                maxWidth: { xs: '100px', sm: '150px' },
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {data ? 'Freie Plätze' : 'Auto Stellplätze'}
            </Typography>
          </Stack>
        </Stack>

        <Stack direction="row">
          <CircleIconValue color="info" indicator={facility.numParkingSpaceDisabled} tooltip="Behindertenparkplätze">
            <WheelchairIcon size={ICON_SIZE} />
          </CircleIconValue>
          <CircleIconValue color="info" indicator={facility.numParkingSpaceWomen} tooltip="Frauenparkplätze">
            <GenderFemaleIcon size={ICON_SIZE} />
          </CircleIconValue>
          <CircleIconValue color="info" indicator={facility.numParkingSpaceMotorcycle} tooltip="Motorradparkplätze">
            <MotorcycleIcon size={ICON_SIZE} />
          </CircleIconValue>
        </Stack>
      </Grid>

      <Grid display="flex" flex={1} flexDirection="column" justifyContent="space-between" size={6}>
        <Stack mb={2}>{forecast && forecast.length > 0 ? <ForecastBars items={forecast} /> : null}</Stack>

        <Stack justifyContent="center">
          {facility.isAlwaysOpen ? (
            <CircleIconValue color="secondary" label="24 h geöffnet">
              <ClockClockwiseIcon size={ICON_SIZE} />
            </CircleIconValue>
          ) : null}
          {facility.freeToUse ? (
            <CircleIconValue color="secondary" label="Kostenfrei Nutzbar">
              <CurrencyEurIcon size={ICON_SIZE} />
            </CircleIconValue>
          ) : null}
          {facility.isParkingUnlimited ? (
            <CircleIconValue color="secondary" label="Parkdauer unbegrenzt">
              <TimerIcon size={ICON_SIZE} />
            </CircleIconValue>
          ) : null}
        </Stack>

        <Stack direction="row">
          {'toStationAccessibility' in facility && (
            <CircleIconValue
              color="info"
              indicator={facility.toStationAccessibility}
              tooltip="Barrierefreiheit zur Station"
            >
              <WheelchairRamp />
            </CircleIconValue>
          )}

          <CircleIconValue color="info" indicator={facility.lighting} tooltip="Beleuchtung">
            <Lighting />
          </CircleIconValue>
        </Stack>
      </Grid>
    </Grid>
  );
}
